import { Grid, Card, CardHeader, CardContent, Stack, Box, Typography, LinearProgress } from '@mui/material';
import { EvStation, Verified, ConfirmationNumber, SpeedOutlined, Bolt, Warning, Healing } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { amcDashboardApi } from '../../api/amc.api';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const HEALTH_COLORS: Record<string, string> = { healthy: '#16a34a', warning: '#d97706', critical: '#dc2626', inactive: '#6b7280' };

export function AmcDashboardPage() {
  const analytics = useQuery({ queryKey: ['amc', 'analytics'], queryFn: () => amcDashboardApi.analytics() });
  const health = useQuery({ queryKey: ['amc', 'health'], queryFn: () => amcDashboardApi.assetHealth() });
  const warranty = useQuery({ queryKey: ['amc', 'warranty'], queryFn: () => amcDashboardApi.warranty() });
  const revenue = useQuery({ queryKey: ['amc', 'revenue'], queryFn: () => amcDashboardApi.revenue() });
  const a = analytics.data;
  const h = health.data;
  const totalHealth = h ? h.healthy + h.warning + h.critical + h.inactive : 0;

  return (
    <>
      <PageHeader title="Service Analytics" subtitle="AMC, SLA, reliability and revenue across the installed fleet." />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#1e40af" label="Total Assets" value={a?.totalAssets ?? 0} icon={<EvStation />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#16a34a" label="Active AMC" value={a?.activeAmc ?? 0} icon={<Verified />} hint={`Expired ${a?.expiredAmc ?? 0}`} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#d97706" label="Open Tickets" value={a?.openTickets ?? 0} icon={<ConfirmationNumber />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#dc2626" label="SLA Breach %" value={`${a?.slaBreachPct ?? 0}%`} icon={<Warning />} /></Grid>
      </Grid>
      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={4} lg={2}><StatCard color="#0d9488" label="MTTR (hrs)" value={a?.mttrHours ?? 0} icon={<SpeedOutlined />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard color="#7c3aed" label="MTBF (hrs)" value={a?.mtbfHours ?? 0} icon={<Healing />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard color="#16a34a" label="First Time Fix" value={`${a?.firstTimeFixPct ?? 0}%`} icon={<Verified />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard color="#2563eb" label="Uptime %" value={`${a?.chargerUptimePct ?? 0}%`} icon={<Bolt />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard color="#b45309" label="Downtime (hrs)" value={a?.downtimeHours ?? 0} icon={<Warning />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard color="#6b7280" label="Technicians" value={a?.technicianProductivity.length ?? 0} icon={<Healing />} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Asset Health" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent>
              {analytics.isLoading && <LinearProgress />}
              <Stack spacing={1.5}>
                {(['healthy', 'warning', 'critical', 'inactive'] as const).map((k) => {
                  const v = h?.[k] ?? 0; const pct = totalHealth ? (v / totalHealth) * 100 : 0;
                  return (
                    <Box key={k}>
                      <Stack direction="row" justifyContent="space-between"><Typography variant="body2" textTransform="capitalize">{k}</Typography><Typography variant="body2" fontWeight={700}>{v}</Typography></Stack>
                      <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: HEALTH_COLORS[k] } }} />
                    </Box>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Warranty & AMC" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent>
              <Stack spacing={1.5}>
                <Row label="In Warranty" value={warranty.data?.inWarranty ?? 0} />
                <Row label="Out of Warranty" value={warranty.data?.outOfWarranty ?? 0} />
                <Row label="AMC Active" value={warranty.data?.amcActive ?? 0} color="#16a34a" />
                <Row label="AMC Expired" value={warranty.data?.amcExpired ?? 0} color="#dc2626" />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Revenue" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent>
              <Stack spacing={1.5}>
                <Row label="AMC Revenue" value={inr(revenue.data?.amcRevenue)} />
                <Row label="Spare Revenue" value={inr(revenue.data?.spareRevenue)} />
                <Row label="Service Revenue" value={inr(revenue.data?.serviceRevenue)} />
                <Row label="Renewal Forecast (90d)" value={inr(revenue.data?.renewalForecast)} color="#2563eb" />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}

function Row({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="subtitle1" fontWeight={700} sx={{ color }}>{value}</Typography>
    </Stack>
  );
}
